import { List, ListItem, makeStyles, Typography } from "@material-ui/core";
import { useFormikContext } from "formik";
import React from "react";
import { FeeStatus, IdentificationStatus, RegistrationStatus } from "../../../Base/AdoptionConstants";
import FormikCheckboxField from "../../../Base/Components/FormikCheckboxField";
import PaymentForm from "../PaymentForm";
import VerifyIdentityForm from "../VerifyIdentityForm";

const useStyles = makeStyles((theme) => ({
  paper: {
    padding: theme.spacing(1),
    marginTop: theme.spacing(2),
  },
  checkboxField: {
    marginLeft: theme.spacing(1.5),
  },
}));

function getHaveAnySearchFieldsChanged(initialValues, currentValues) {
  return (
    initialValues.adopteeAdoptiveFirstName !== currentValues.adopteeAdoptiveFirstName ||
    initialValues.adopteeAdoptiveLastName !== currentValues.adopteeAdoptiveLastName ||
    initialValues.adopteeAdoptiveMiddleName !== currentValues.adopteeAdoptiveMiddleName ||
    initialValues.adopteeBirthFirstName !== currentValues.adopteeBirthFirstName ||
    initialValues.adopteeBirthLastName !== currentValues.adopteeBirthLastName ||
    initialValues.adopteeBirthMiddleName !== currentValues.adopteeBirthMiddleName ||
    initialValues.adopteeCurrentFirstName !== currentValues.adopteeCurrentFirstName ||
    initialValues.adopteeCurrentLastName !== currentValues.adopteeCurrentLastName ||
    initialValues.adopteeCurrentMiddleName !== currentValues.adopteeCurrentMiddleName ||
    initialValues.adoptionAgencyAttorney !== currentValues.adoptionAgencyAttorney ||
    initialValues.adoptiveParent1FirstName !== currentValues.adoptiveParent1FirstName ||
    initialValues.adoptiveParent1LastName !== currentValues.adoptiveParent1LastName ||
    initialValues.adoptiveParent1MiddleName !== currentValues.adoptiveParent1MiddleName ||
    initialValues.adoptiveParent2FirstName !== currentValues.adoptiveParent2FirstName ||
    initialValues.adoptiveParent2LastName !== currentValues.adoptiveParent2LastName ||
    initialValues.adoptiveParent2MiddleName !== currentValues.adoptiveParent2MiddleName ||
    initialValues.birthCity !== currentValues.birthCity ||
    initialValues.birthDate !== currentValues.birthDate ||
    initialValues.birthDateRangeEnd !== currentValues.birthDateRangeEnd ||
    initialValues.birthDateRangeStart !== currentValues.birthDateRangeStart ||
    initialValues.birthHospital !== currentValues.birthHospital ||
    initialValues.birthMotherAliasFirstName !== currentValues.birthMotherAliasFirstName ||
    initialValues.birthMotherAliasLastName !== currentValues.birthMotherAliasLastName ||
    initialValues.birthMotherAliasMiddleName !== currentValues.birthMotherAliasMiddleName ||
    initialValues.birthParent1CertFirstName !== currentValues.birthParent1CertFirstName ||
    initialValues.birthParent1CertLastName !== currentValues.birthParent1CertLastName ||
    initialValues.birthParent1CertMiddleName !== currentValues.birthParent1CertMiddleName ||
    initialValues.birthParent1CurrentFirstName !== currentValues.birthParent1CurrentFirstName ||
    initialValues.birthParent1CurrentLastName !== currentValues.birthParent1CurrentLastName ||
    initialValues.birthParent1CurrentMiddleName !== currentValues.birthParent1CurrentMiddleName ||
    initialValues.birthParent2CertFirstName !== currentValues.birthParent2CertFirstName ||
    initialValues.birthParent2CertLastName !== currentValues.birthParent2CertLastName ||
    initialValues.birthParent2CertMiddleName !== currentValues.birthParent2CertMiddleName ||
    initialValues.birthParent2CurrentFirstName !== currentValues.birthParent2CurrentFirstName ||
    initialValues.birthParent2CurrentLastName !== currentValues.birthParent2CurrentLastName ||
    initialValues.birthParent2CurrentMiddleName !== currentValues.birthParent2CurrentMiddleName ||
    initialValues.birthSex !== currentValues.birthSex ||
    initialValues.otherBirthLocation !== currentValues.otherBirthLocation
  );
}

export default function PaymentConfirmationForm() {
  const classes = useStyles();
  const formik = useFormikContext();

  const showIdentityForm = React.useMemo(
    () => ![IdentificationStatus.VERIFIED.value].includes(formik.values.identificationStatus),
    [formik.values.identificationStatus]
  );

  const showPaymentForm = React.useMemo(
    () =>
      !formik.values.feeStatus ||
      [FeeStatus.FEE_PENDING.value, FeeStatus.PREV_PAID_PENDING.value].includes(formik.values.feeStatus),
    [formik.values.feeStatus]
  );

  React.useEffect(() => {
    // Never show update fee unless registration is searched_no_match
    if (RegistrationStatus.SEARCHED_NO_MATCH.value === formik.values.registrationStatus) {
      const haveAnySearchFieldsChanged = getHaveAnySearchFieldsChanged(formik.initialValues, formik.values);
      // If changes were made and not already requiring the update fee, require it
      if (haveAnySearchFieldsChanged && formik.values.requireUpdateFee === false) {
        formik.setFieldValue("requireUpdateFee", true);
        formik.setFieldValue("agreeToUpdateFee", false);
      }
      // If no changes were made but fee set to required, stop requiring it
      else if (!haveAnySearchFieldsChanged && formik.values.requireUpdateFee === true) {
        formik.setFieldValue("requireUpdateFee", false);
        formik.setFieldValue("agreeToUpdateFee", false);
      }
    } else if (
      // If registration is still pending fee from changes, show the field as already checked
      formik.values.registrationStatus === RegistrationStatus.NEEDS_RESEARCH.value &&
      formik.values.feeStatus === FeeStatus.UPDATE_FEE_PENDING.value &&
      formik.values.requireUpdateFee === false
    ) {
      formik.setFieldValue("requireUpdateFee", true);
      formik.setFieldValue("agreeToUpdateFee", true);
    }
  }, [showPaymentForm, formik]);

  return (
    <div role="main">
      {showIdentityForm && (
        <>
          <Typography variant="h3" className="h3-header">
            ID Verification
          </Typography>
          <VerifyIdentityForm registrationId={formik.values.registrationId} />
        </>
      )}
      {showPaymentForm && (
        <>
          <Typography variant="h3" className="h3-header">
            Payment
          </Typography>
          <PaymentForm />
        </>
      )}
      {formik.values.requireUpdateFee && (
        <>
          <Typography variant="h3" className="h3-header">
            Update Fee Required
          </Typography>
          <Typography variant="body1" gutterBottom>
            You have updated information that is used in attempting to find a match for you. A fee of $10 is required in
            order for your updated information to be processed again.
          </Typography>

          <FormikCheckboxField
            className={classes.checkboxField}
            name="agreeToUpdateFee"
            label={
              <>
                <Typography variant="body1" gutterBottom>
                  I agree to mail a $10 check to the Office of Vital Records and Statistics:
                </Typography>
                <div style={{ marginLeft: 16 }}>
                  <Typography variant="body2">
                    Vital Records - Utah Adoption Registry
                    <br />
                    P.O. Box 141012
                    <br />
                    Salt Lake City, UT 84114
                  </Typography>
                </div>
              </>
            }
          />
        </>
      )}
      <Typography variant="h3" className="h3-header">
        Disclaimer
      </Typography>
      <List>
        <ListItem>I hereby consent to share information with any match as I have elected in this form.</ListItem>
        <ListItem>I understand I can log in and change my elections or update my information at any time.</ListItem>
        <ListItem>
          My information will remain in the Utah Adoption Registry unless I change it by logging into the system or
          submitting an update form.
        </ListItem>

        <ListItem>I understand that registering here does not guarantee a match will be found.</ListItem>
      </List>
      <FormikCheckboxField
        className={classes.checkboxField}
        name="agreeToDisclaimer"
        label="I have read and agree to this disclaimer"
      />
    </div>
  );
}
